import { Link } from "react-router-dom";
import { ArrowUpRight, Briefcase, Building, Building2, House, LandPlot, Rows3, Store } from "lucide-react";
import { mediaUrl } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import { useBi } from "../../lib/customer";
import { useSite } from "../../lib/site";
import { Reveal, SectionHead } from "../Common";
import { CityMap } from "../Maps";
import { Rail, useNum } from "./Kit";

const TYPES = [["apartment", Building2], ["villa", House], ["townhouse", Rows3], ["office", Briefcase], ["retail", Store], ["land", LandPlot], ["commercial_building", Building]];

export function TypeGrid({ counts = {} }) {
  const b = useBi();
  const { label } = useSite();
  const n = useNum();
  return (
    <section data-testid="home-types" className="container-lux py-24">
      <SectionHead eyebrow={b("Property types", "أنواع العقارات")} title={b("Browse by property type", "تصفّح حسب نوع العقار")} />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-7">
        {TYPES.map(([key, Icon], i) => (
          <Reveal key={key} delay={i * 0.05}>
            <Link to={`/properties?type=${key}`} data-testid={`home-type-${key}`} className="group flex h-full flex-col justify-between gap-8 rounded-sm border border-fg/10 bg-surface p-5 transition-[border-color,transform] duration-300 hover:-translate-y-1 hover:border-cta/60">
              <Icon className="h-7 w-7 text-cta transition-transform duration-300 group-hover:scale-110" strokeWidth={1.4} />
              <div>
                <p className="font-display text-lg leading-tight text-fg">{label("property_type", key)}</p>
                <p className="mt-1 text-xs text-fg/50" data-testid={`home-type-count-${key}`}>{n(counts[key] || 0)} {b("listings", "عقار")}</p>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function CityCard({ c }) {
  const b = useBi();
  const { tr } = useI18n();
  const n = useNum();
  return (
    <Link to={`/properties?city=${c.slug}`} data-testid={`home-city-card-${c.slug}`} className="on-media group relative block aspect-[3/4] overflow-hidden rounded-sm border border-fg/10">
      {c.image_url && <img src={mediaUrl(c.image_url)} alt={tr(c, "name")} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1400ms] group-hover:scale-110" />}
      <div className="absolute inset-0 bg-gradient-to-t from-canvas via-canvas/30 to-transparent" aria-hidden="true" />
      <ArrowUpRight className="rtl-flip absolute end-4 top-4 h-5 w-5 text-fg/70 transition-colors group-hover:text-cta" />
      <div className="absolute inset-x-0 bottom-0 p-5">
        <p className="font-display text-2xl text-fg">{tr(c, "name")}</p>
        <p className="mt-1 text-xs text-fg/65">{n(c.properties)} {b("properties", "عقار")} · {n(c.projects)} {b("projects", "مشروع")}</p>
      </div>
    </Link>
  );
}

export function Cities({ cards = [], map = [] }) {
  const b = useBi();
  const { t, tr } = useI18n();
  return (
    <section data-testid="home-cities" className="relative border-y border-cta/10 bg-surface py-24">
      <div className="mashrabiya pointer-events-none absolute inset-0 opacity-[0.03]" aria-hidden="true" />
      <div className="container-lux relative">
        <SectionHead eyebrow={t("home.mapTitle")} title={b("Explore by city", "استكشف حسب المدينة")} sub={t("home.mapSub")} />
        <Rail testid="home-city-rail" className="md:grid-cols-3 lg:grid-cols-6">
          {cards.map((c, i) => (<Reveal key={c.slug} delay={i * 0.06} className="w-[62%] shrink-0 snap-start md:w-auto"><CityCard c={c} /></Reveal>))}
        </Rail>
        <div className="mt-16 grid gap-12 lg:grid-cols-12">
          <ul className="space-y-2 lg:col-span-4">
            {map.map((c) => (
              <li key={c.slug}><Link to={`/properties?city=${c.slug}`} data-testid={`home-city-link-${c.slug}`} className="flex items-center justify-between border-b border-fg/10 py-3 text-fg/80 transition-colors hover:text-cta"><span className="font-display text-xl">{tr(c, "name")}</span><span className="text-xs text-fg/50">{c.properties} · {c.projects}</span></Link></li>
            ))}
          </ul>
          <Reveal className="lg:col-span-8"><CityMap cities={map} /></Reveal>
        </div>
      </div>
    </section>
  );
}
